import React, { useContext, useState, useEffect } from "react";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Form, Button, Container } from "react-bootstrap";
import { TranslatorContext } from "../../../context/Translator";
import { Link } from "react-router-dom";
import axios from "axios";

export default function DashboardPage() {
  const { t } = useContext(TranslatorContext);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    user: {},
    stats: {},
    recent_products: []
  });

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: ""
  });

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    const todayFilters = {
      start_date: getTodayDate(),
      end_date: getTodayDate()
    };

    setFilters(todayFilters);
    fetchDashboardData(todayFilters);
  }, []);

  const fetchDashboardData = async (filterParams = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterParams.start_date) params.append('start_date', filterParams.start_date);
      if (filterParams.end_date) params.append('end_date', filterParams.end_date);

      const url = `${process.env.REACT_APP_API_URL}/dashboard${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`
        }
      });

      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchDashboardData(filters);
  };

  const resetFilters = () => {
    const today = getTodayDate();
    const resetData = {
      start_date: today,
      end_date: today
    };

    setFilters(resetData);
    fetchDashboardData(resetData);
  };

  if (loading) {
    return (
      <PageLayout>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <div className="spinner"></div>
          <p>Loading Dashboard...</p>
        </div>
      </PageLayout>
    );
  }

  const { stats = {}, recent_products = [] } = dashboardData;

  return (
    <PageLayout>
      <Container fluid className="px-0">
        {/* Header Breadcrumb */}
        <Row>
          <Col xl={12}>
            <div className="mc-card">
              <div className="mc-breadcrumb">
                <h3 className="mc-breadcrumb-title">
                  {t("dashboard") || "Dashboard"}
                </h3>
                <ul className="mc-breadcrumb-list">
                  <li className="mc-breadcrumb-item">
                    <Link to="#" className="mc-breadcrumb-link">
                      {t("home") || "Home"}
                    </Link>
                  </li>
                  <li className="mc-breadcrumb-item">
                    {t("dashboard") || "Dashboard"}
                  </li>
                </ul>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Section */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="filter-card">
              <div className="filter-header">
                <i className="material-icons">filter_alt</i>
                <h5>Filter Dashboard</h5>
              </div>
              <Row className="align-items-end">
                <Col xs={12} md={5} lg={4} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="start_date"
                      value={filters.start_date}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={5} lg={4} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="end_date"
                      value={filters.end_date}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <div className="filter-buttons">
                    <Button onClick={applyFilters} className="btn btn-primary me-2">
                      <i className="material-icons">search</i> Apply
                    </Button>
                    <Button variant="secondary" onClick={resetFilters}>
                      <i className="material-icons">refresh</i> Reset
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Core Overview Stats */}
        <Row>
          <Col xs={12} sm={6} xl={3} className="mb-3">
            <div className="stat-card">
              <div className="stat-icon bg-primary">
                <i className="material-icons">people</i>
              </div>
              <div className="stat-content">
                <h3>{stats.total_users || 0}</h3>
                <p>Total Users</p>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} xl={3} className="mb-3">
            <div className="stat-card">
              <div className="stat-icon bg-success">
                <i className="material-icons">watch</i>
              </div>
              <div className="stat-content">
                <h3>{stats.total_products || 0}</h3>
                <p>Total Products</p>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} xl={3} className="mb-3">
            <div className="stat-card">
              <div className="stat-icon bg-info">
                <i className="material-icons">sensors</i>
              </div>
              <div className="stat-content">
                <h3>{stats.live_products || 0}</h3>
                <p>Live Products</p>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} xl={3} className="mb-3">
            <div className="stat-card">
              <div className="stat-icon bg-warning">
                <i className="material-icons">pending_actions</i>
              </div>
              <div className="stat-content">
                <h3>{stats.pending_checker_verification || 0}</h3>
                <p>Pending Verification</p>
              </div>
            </div>
          </Col>
        </Row>


        {/* Roles Breakdown */}
        <Row className="mb-4">
          <Col xs={12}>
            <div className="mc-card">
              <h4 className="card-title">User Roles Overview</h4>
              <Row>
                <Col xs={6} sm={4} md={2} className="mb-3">
                  <div className="finance-item">
                    <span>Issuers</span>
                    <strong className="text-primary">{stats.total_issuers || 0}</strong>
                  </div>
                </Col>
                <Col xs={6} sm={4} md={2} className="mb-3">
                  <div className="finance-item">
                    <span>Listers</span>
                    <strong className="text-info">{stats.total_listers || 0}</strong>
                  </div>
                </Col>
                <Col xs={6} sm={4} md={2} className="mb-3">
                  <div className="finance-item">
                    <span>Checkers</span>
                    <strong className="text-warning">{stats.total_checkers || 0}</strong>
                  </div>
                </Col>
                <Col xs={6} sm={4} md={2} className="mb-3">
                  <div className="finance-item">
                    <span>Stock Keepers</span>
                    <strong className="text-secondary">{stats.total_stock_keepers || 0}</strong>
                  </div>
                </Col>
                <Col xs={6} sm={4} md={2} className="mb-3">
                  <div className="finance-item">
                    <span>Marketplacers</span>
                    <strong className="text-success">{stats.total_market_placers || 0}</strong>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        {/* Recent Products Table */}
        <Row>
          <Col xs={12} className="mb-4">
            <div className="mc-card h-100">
              <h4 className="card-title">
                Recent Products
                {recent_products.length > 0 && (
                  <span className="table-badge">{recent_products.length}</span>
                )}
              </h4>
              {recent_products.length > 0 ? (
                <div className="table-responsive">
                  <table className="simple-table">
                    <thead>
                      <tr>
                        <th>Product Details</th>
                        <th>Category</th>
                        <th>Issuer</th>
                        <th>Style & Buying Price</th>
                        <th>Created At</th>
                        <th>View Timeline</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent_products.map((product) => (
                        <tr key={product.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              {product.first_img && (
                                <img
                                  src={process.env.REACT_APP_IMG_URL + product.first_img}
                                  alt={product.title}
                                  style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px", marginRight: "10px" }}
                                />
                              )}
                              <div>
                                <strong>{product.title}</strong>
                                <br />
                                <small className="text-muted">{product.product_code}</small>
                              </div>
                            </div>
                          </td>
                          <td>{product.category?.name || "N/A"}</td>
                          <td>{product.issuer?.name || "N/A"}</td>
                          <td>
                            <strong>${product.buying_price}</strong>
                            <br />
                            <small className="text-muted">{product.selling_style}</small>
                          </td>
                          <td>
                            {new Date(product.created_at).toLocaleDateString()}
                          </td>
                          <td>
                            <Link to={`/product-timeline/${product.id}`}><i className="material-icons view">visibility</i></Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <i className="material-icons">watch_off</i>
                  <p>No recent products found</p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </PageLayout>
  );
}